/**
 * docParser.js — Client-side document content extraction
 *
 * Supports:
 *   - PDF  → text via pdfjs-dist
 *   - DOCX → text via mammoth
 *   - Images (PNG/JPG/WebP/GIF) → base64 (for Gemini multimodal)
 *   - Plain text (.txt, .md, .csv) → raw text
 */

// ---------------------------------------------------------------------------
// PDF extraction
// ---------------------------------------------------------------------------

async function extractPdfText(file) {
  const pdfjsLib = await import('pdfjs-dist/build/pdf');
  // Use the bundled worker to avoid CORS issues with CDN
  const pdfjsWorker = await import('pdfjs-dist/build/pdf.worker.entry');
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const pages = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const text = content.items.map((item) => item.str).join(' ');
    pages.push(text);
  }

  return pages.join('\n\n');
}

// ---------------------------------------------------------------------------
// DOCX extraction
// ---------------------------------------------------------------------------

async function extractDocxText(file) {
  const mammoth = await import('mammoth');
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value;
}

// ---------------------------------------------------------------------------
// Image → base64
// ---------------------------------------------------------------------------

function imageToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // reader.result = "data:image/png;base64,..."
      const base64 = reader.result.split(',')[1];
      resolve({ base64, mimeType: file.type });
    };
    reader.onerror = () => reject(new Error('Failed to read image file.'));
    reader.readAsDataURL(file);
  });
}

// ---------------------------------------------------------------------------
// Plain text
// ---------------------------------------------------------------------------

function readAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read text file.'));
    reader.readAsText(file);
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const TEXT_EXTENSIONS = ['.txt', '.md', '.csv'];

/**
 * Extract content from a file for use with Gemini.
 * Returns { type: 'text'|'image', text?, imageBase64?, imageMime? }
 */
export async function extractContent(file) {
  const name = file.name.toLowerCase();
  const mime = file.type;

  // Images → base64 for Gemini multimodal
  if (IMAGE_TYPES.includes(mime) || name.match(/\.(png|jpe?g|webp|gif)$/)) {
    const { base64, mimeType } = await imageToBase64(file);
    return { type: 'image', imageBase64: base64, imageMime: mimeType };
  }

  // PDF
  if (mime === 'application/pdf' || name.endsWith('.pdf')) {
    const text = await extractPdfText(file);
    return { type: 'text', text };
  }

  // DOCX
  if (
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    name.endsWith('.docx')
  ) {
    const text = await extractDocxText(file);
    return { type: 'text', text };
  }

  // Plain text files
  if (TEXT_EXTENSIONS.some((ext) => name.endsWith(ext)) || mime.startsWith('text/')) {
    const text = await readAsText(file);
    return { type: 'text', text };
  }

  throw new Error(`Unsupported file type: ${name} (${mime}). Supported: PDF, DOCX, PNG, JPG, WebP, GIF, TXT, CSV.`);
}

/**
 * Get a human-readable label for supported file types.
 */
export const SUPPORTED_FILE_TYPES = 'PDF, DOCX, PNG, JPG, WebP, GIF, TXT';
